import * as React from "react";
import cx from "classnames";

import { Flex } from "@common/components/Flex";
import { trDts } from "@modules/datasheets/utils/i18n";
import { Checkbox } from "@common/components/checkbox";
import { getTestAttributes } from "@common/utils/tests";
import { TextArea } from "@common/components/text-area";
import { FormControl } from "@common/components/form-control";
import { Button } from "@common/components/Button";
import Dropdown, { DROPDOWN_NO_VALUE } from "@common/containers/fields/Dropdown";
import { FieldType } from "@common/dto/field/type";
import { NON_TAB_FIELD_TYPES } from "@modules/datasheets/utils/tab-navigation";
import {
  DatasheetElementSettingsProps,
  useDatasheetElementSettingsController,
} from "./controllers/main";
import { TextStyleManagement } from "./components/text-style-management/text-style-management";
import { ConfigureTable } from "./components/configure-table/configure-table";

import { useMessage } from '@common/hooks/message';
import { MessageBox } from '@common/components/MessageBox';

import "./datasheet-element-settings.styles.scss";

export const DatasheetElementSettings: React.FC<DatasheetElementSettingsProps> = (props) => {
  const { className, fieldComponentValue, textComponentValue, title, datasheetElement, onNewTableDatasheet, tabEligibleFieldCount, selectedDatasheetElementsCount } = props;
  const {
    currentFieldComponentValue,
    currentTextComponentValue,
    contentController,
    headingController,
    tableController,
    addedTextController,
    textStyleOptions,
    textAlignOptions,
    fontFamilyOptions,
    fontSizesOptions,
    headingAlignOptions,
    expanded,
    toggleExpanded,
    handleHelpTextChange,
  } = useDatasheetElementSettingsController(props);

  const { showErrorMessage, closeMessage, messageProps } = useMessage();

  const [localTabOrder, setLocalTabOrder] = React.useState<string>(
    String(fieldComponentValue?.content.tabOrder ?? '')
  );
  const isTabOrderFocused = React.useRef(false);
  const prevElementId = React.useRef<string | undefined>(datasheetElement?.id);

  React.useEffect(() => {
    if (datasheetElement?.id !== prevElementId.current) {
      prevElementId.current = datasheetElement?.id;
      setLocalTabOrder(String(fieldComponentValue?.content.tabOrder ?? ''));
      return;
    }
    if (!isTabOrderFocused.current) {
      setLocalTabOrder(String(fieldComponentValue?.content.tabOrder ?? ''));
    }
  }, [datasheetElement?.id, fieldComponentValue?.content.tabOrder]);

  const commitTabOrder = React.useCallback(() => {
    const parsed = parseInt(localTabOrder, 10);
    const max = tabEligibleFieldCount ?? 0;

    if (isNaN(parsed) || parsed < 1 || (max > 0 && parsed > max)) {
      showErrorMessage(
        max > 0
          ? `Tab order must be between 1 and ${max}.`
          : 'Tab order must be a positive number.'
      );
      setLocalTabOrder(String(fieldComponentValue?.content.tabOrder ?? ''));
      return;
    }

    contentController.handleTabOrderChange(parsed);
  }, [localTabOrder, contentController, fieldComponentValue?.content.tabOrder, tabEligibleFieldCount, showErrorMessage]);

  const handleTabOrderKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitTabOrder();
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const cur = parseInt(localTabOrder, 10);
      const next = (isNaN(cur) ? 0 : cur) + 1;
      const clamped = tabEligibleFieldCount != null ? Math.min(next, tabEligibleFieldCount) : next;
      setLocalTabOrder(String(clamped));
      contentController.handleTabOrderChange(clamped);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const cur = parseInt(localTabOrder, 10);
      const clamped = Math.max(1, (isNaN(cur) ? 2 : cur) - 1);
      setLocalTabOrder(String(clamped));
      contentController.handleTabOrderChange(clamped);
    }
  }, [commitTabOrder, localTabOrder, tabEligibleFieldCount, setLocalTabOrder, contentController]);

  const fieldSelected = Boolean(fieldComponentValue);
  const isMultiFieldSelection = Boolean(fieldSelected && (selectedDatasheetElementsCount ?? 0) > 1);

  const selectedOriginField = datasheetElement?.original?.field;
  const resolvedSelectedFieldTypeId =
    (selectedOriginField?.fieldtype?.typeid as number | undefined) ??
    ((selectedOriginField as any)?.rawFieldType as number | undefined) ??
    ((selectedOriginField as any)?.type as number | undefined);
  const hasSelectedTableSubfields = Array.isArray((selectedOriginField as any)?.subfields) &&
    (selectedOriginField as any).subfields.length > 0;

  const isSelectedTableField = Boolean(
    fieldSelected &&
      (
        resolvedSelectedFieldTypeId === FieldType.table ||
        Boolean(selectedOriginField?.table) ||
        hasSelectedTableSubfields
      )
  );
  const selectedTableField = React.useMemo(() => {
    if (!isSelectedTableField || !selectedOriginField) return null;
    const rawCtx = (selectedOriginField as any).context ?? selectedOriginField.fieldcontext;
    let ctx = typeof rawCtx === "number" ? rawCtx : Number(rawCtx);
    if (Number.isNaN(ctx)) {
      if (rawCtx === "IDS_CONTEXT") ctx = 1;
      else if (rawCtx === "PDS_CONTEXT") ctx = 2;
      else if (rawCtx === "TABLE_CONTEXT") ctx = 8;
      else ctx = 1;
    }
    return {
      id: selectedOriginField.fieldid,
      name: selectedOriginField.fieldname,
      context: ctx,
    };
  }, [isSelectedTableField, selectedOriginField]);

  const configureTableTitle = React.useMemo(() => {
    if (selectedTableField?.name) {
      return `${trDts("Configure Table")} ${selectedTableField.name}`;
    }
    return trDts("Configure Table");
  }, [selectedTableField?.name]);

  const handleDefaultTableDatasheetChange = (value: string | number | typeof DROPDOWN_NO_VALUE) => {
    const selectedFormat = tableController.tableDatasheetFormats?.find(
      (format) => String(format.id) === String(value)
    );
    if (selectedFormat) {
      tableController.onSetDefaultTableDatasheet?.(selectedFormat.id);
    }
  };
  const fieldDisplayInfo = React.useMemo(() => {
    if (!fieldSelected || !datasheetElement) return null;
    const field = datasheetElement.original.field;
    const fieldPath = field?.fieldnameWithPath || field?.fieldname || '';
    const fieldType = field?.fieldtype?.typename || '';
    return { fieldPath, fieldType };
  }, [fieldSelected, datasheetElement]);

  return (
    <div
      className={cx(
        "DatasheetsEditor__datasheet-element-settings",
        { "DatasheetsEditor__datasheet-element-settings--expanded": expanded },
        className
      )}
    >
      <Flex style={{ alignItems: 'flex-start', minWidth: 0 }}>
        <button
          {...getTestAttributes({ id: "fp-datasheet-design-right-panel-toggle-btn" })}
          className="DatasheetsEditor__datasheet-element-settings__toggler"
          onClick={toggleExpanded}
          style={{ flexShrink: 0 }}
        >
          <img src={expanded ? "png/arrow_right.png" : "png/arrow_left.png"} />
        </button>
        {expanded && (
          <div className="DatasheetsEditor__datasheet-element-settings__title">
            {textComponentValue ? (
              <p>{trDts('Added Text')}</p>
            ) : isMultiFieldSelection ? (
              <>
                <p>{trDts("Multiple fields selected")}</p>
                <div className="DatasheetsEditor__datasheet-element-settings__helper-banner">
                  <span
                    className="fa fa-info-circle DatasheetsEditor__datasheet-element-settings__helper-banner-icon"
                    aria-hidden="true"
                  />
                  {trDts("multiFieldSelectionHelperText")}
                </div>
              </>
            ) : fieldSelected && fieldDisplayInfo ? (
              <>
                <p style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  {fieldDisplayInfo.fieldPath}
                </p>
                {fieldDisplayInfo.fieldType && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '2px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                    ({fieldDisplayInfo.fieldType.toLowerCase()} field)
                  </p>
                )}
              </>
            ) : (
              <p>{trDts("Default Settings")}</p>
            )}
          </div>
        )}
      </Flex>

      <div className="DatasheetsEditor__datasheet-element-settings__content">
        {!textComponentValue && (
          <>
            <TextStyleManagement
              fontFamilyOptions={fontFamilyOptions}
              fontFamily={currentFieldComponentValue.content.fontFamily}
              color={currentFieldComponentValue.content.color}
              fontSizesOptions={fontSizesOptions}
              fontSize={currentFieldComponentValue.content.fontSize}
              textAlignOptions={textAlignOptions}
              textStyles={currentFieldComponentValue.content.styles}
              headingPositionOptions={headingController.positions}
              textAlign={currentFieldComponentValue.content.align}
              textStylesOptions={textStyleOptions}
              onFontFamilyChange={contentController.handleFontFamilyChange}
              onColorChange={contentController.handleColorChange}
              onFontSizeChange={contentController.handleFontSizeChange}
              onTextStylesChange={contentController.handleStylesChange}
              onTextAlignsChange={contentController.handleAlignChange}
              getId={{
                font: getTestAttributes({ id: "data-font-list" }).id,
                fontSize: getTestAttributes({ id: "data-font-size-list" }).id,
                colorPicker: getTestAttributes({ id: "fp-datasheet-design-right-panel-datacolorbtn" }).id,
              }}
            />

            {fieldSelected
              && !isMultiFieldSelection
              && !selectedOriginField?.system
              && !NON_TAB_FIELD_TYPES.has(resolvedSelectedFieldTypeId as FieldType)
              && (
              <Flex alignItems="center" className="DatasheetsEditor__datasheet-element-settings__section">
                <span style={{ marginRight: "4px" }}>{trDts("Tab Order")}</span>
                <FormControl
                  {...getTestAttributes({ id: "tx-datasheet-design-right-panel-tab-order" })}
                  value={localTabOrder}
                  type="number"
                  min={1}
                  max={tabEligibleFieldCount ?? undefined}
                  style={{ width: "80px" }}
                  onFocus={() => { isTabOrderFocused.current = true; }}
                  onBlur={() => {
                    isTabOrderFocused.current = false;
                    commitTabOrder();
                  }}
                  onChange={(e) => { setLocalTabOrder(e.target.value); }}
                  onKeyDown={handleTabOrderKeyDown}
                />
              </Flex>
            )}
            <div>
              <Checkbox
                {...getTestAttributes({ id: "cbx-datasheet-design-right-panel-show-border-input" })}
                checked={currentFieldComponentValue.content.showBorder}
                label={trDts("Show Border")}
                onChange={contentController.handleShowBorderChange}
              />
              <Checkbox
                {...getTestAttributes({ id: "cbx-datasheet-design-right-panel-show-field-type-input" })}
                checked={currentFieldComponentValue.content.showFieldType}
                label={trDts("Show Field Type")}
                onChange={contentController.handleShowFieldTypeChange}
              />
            </div>
           <div className="DatasheetsEditor__datasheet-element-settings__table-actions">
           {isSelectedTableField && !isMultiFieldSelection && (
            <>
              <Button
                {...getTestAttributes({ id: "btn-datasheet-design-right-panel-configure-table" })}
                onClick={tableController.openConfigureTable}
                bsStyle="primary"
              >
                {trDts("Configure Table")}
              </Button>
              <Button
                {...getTestAttributes({ id: "btn-datasheet-design-right-panel-new-table-datasheet" })}
                 bsStyle="primary"
                onClick={() => {
                  if (selectedTableField) {
                    onNewTableDatasheet?.(selectedTableField);
                  }
                }}
              >
                {trDts("New Table Datasheet")}
              </Button>

              <div className="DatasheetsEditor__datasheet-element-settings__table-default-datasheet" style={{marginTop: '8px'}}>
                    <span>
                      {trDts("Default Table Datasheet")}
                    </span>
                    <Dropdown
                      className="DatasheetsEditor__datasheet-element-settings__table-default-datasheet__dropdown"
                      fullWidth
                      searchable={false}
                      options={tableController.tableDatasheetFormats.map((format) => ({
                        label: format.name,
                        value: String(format.id),
                      }))}
                      selectedValue={
                        tableController.selectedTableDatasheetFormatId != null
                          ? String(tableController.selectedTableDatasheetFormatId)
                          : DROPDOWN_NO_VALUE
                      }
                      onValueChanged={handleDefaultTableDatasheetChange}
                    />
                  </div>
              </>
            )}
           </div>

            {tableController.configureTableOpened && (
              <ConfigureTable
                title={configureTableTitle}
                dropdownFormats={tableController.formats}
                tableFormats={tableController.tableFormats}
                defaultTableFormats={tableController.defaultTableFormats}
                selectedDropdownFormatId={tableController.selectedDropdownFormatId}
                onClose={tableController.closeConfigureTable}
                onSubmit={tableController.handleSubmitConfigureTable}
                checkFormatNameExists={tableController.checkFormatNameExists}
                onSelectedFormatChange={tableController.onSelectedFormatChange}
                onDeleteFormat={tableController.onDeleteFormat}
                onSetDefaultFormat={tableController.onSetDefaultFormat}
                onTableRowsChange={tableController.onTableRowsChange}
              />
            )}
          </>
        )}

        {!textComponentValue && (
          <>
            <h3 className="DatasheetsEditor__datasheet-element-settings__section__title">{trDts("Heading")}</h3>
            <div className="DatasheetsEditor__datasheet-element-settings__section">
              <Checkbox
                {...getTestAttributes({ id: "cbx-datasheet-design-right-panel-show-display-input" })}
                checked={currentFieldComponentValue.heading.displayHeading}
                label={trDts("Display Heading")}
                onChange={headingController.handleDisplayHeadingChange}
              />
            </div>
            {fieldSelected && (
              <Flex alignItems="center" className="DatasheetsEditor__datasheet-element-settings__section">
                <span style={{ marginRight: "4px" }}>{trDts("Heading")}</span>
                <FormControl
                  {...getTestAttributes({ id: "tx-datasheet-design-right-panel-heading-input" })}
                  value={currentFieldComponentValue.heading.textValue}
                  style={{ marginLeft: "4px" }}
                  disabled={isMultiFieldSelection}
                  onChange={(e) => headingController.handleTextValueChange(e.target.value)}
                />
              </Flex>
            )}

            <TextStyleManagement
              fontFamilyOptions={fontFamilyOptions}
              fontSizesOptions={fontSizesOptions}
              textStylesOptions={textStyleOptions}
              fontFamily={currentFieldComponentValue.heading.fontFamily}
              color={currentFieldComponentValue.heading.color}
              headingAlign={currentFieldComponentValue.heading.align}
              headingAlignOptions={headingAlignOptions}
              fontSize={currentFieldComponentValue.heading.fontSize}
              headingPosition={currentFieldComponentValue.heading.position}
              headingPositionOptions={headingController.positions}
              textStyles={currentFieldComponentValue.heading.textStyles}
              onFontFamilyChange={headingController.handleFontFamilyChange}
              onColorChange={headingController.handleColorChange}
              onHeadingAlignChange={headingController.handleAlignChange}
              onFontSizeChange={headingController.handleFontSizeChange}
              onHeadingPositionChange={headingController.handlePositionChange}
              onTextStylesChange={headingController.handleStylesChange}
              getId={{
                font: getTestAttributes({ id: "head-font-list" }).id,
                fontSize: getTestAttributes({ id: "head-font-size-list" }).id,
                colorPicker: getTestAttributes({ id: "fp-datasheet-design-right-panel-head-color-btn" }).id,
                positions: getTestAttributes({ id: "head-position-list" }).id,
                aligns: getTestAttributes({ id: "align-list" }).id,
              }}
            />
          </>
        )}

        {(textComponentValue || (!fieldSelected && currentTextComponentValue)) && (
          <>
            {!textComponentValue && !fieldSelected && (
              <h3 className="DatasheetsEditor__datasheet-element-settings__section__title">{trDts("Added Text")}</h3>
            )}
            {currentTextComponentValue && (
              <TextStyleManagement
                fontFamilyOptions={fontFamilyOptions}
                fontSizesOptions={fontSizesOptions}
                textStylesOptions={textStyleOptions}
                fontFamily={currentTextComponentValue.fontFamily}
                headingAlignOptions={headingAlignOptions}
                color={currentTextComponentValue.color}
                headingPositionOptions={headingController.positions}
                fontSize={currentTextComponentValue.fontSize}
                textStyles={currentTextComponentValue.textStyle}
                textAlign={currentTextComponentValue.align}
                onFontFamilyChange={addedTextController.handleFontFamilyChange}
                onColorChange={addedTextController.handleColorChange}
                onTextStylesChange={addedTextController.handleStylesChange}
                onFontSizeChange={addedTextController.handleFontSizeChange}
                onTextAlignsChange={addedTextController.handleAlignChange}
                getId={{
                  font: getTestAttributes({ id: "text-font-list" }).id,
                  fontSize: getTestAttributes({ id: "text-font-size-list" }).id,
                  colorPicker: getTestAttributes({ id: "fp-datasheet-design-right-panel-text-color-btn" }).id,
                  aligns: getTestAttributes({ id: "text-align-list" }).id,
                }}
              />
            )}
          </>
        )}
        {fieldSelected && (
          <>
            <h3 className="DatasheetsEditor__datasheet-element-settings__section__title">{trDts("Help Text")}</h3>
            <TextArea
              {...getTestAttributes({ id: "tx-datasheet-design-right-panel-help-text-input" })}
              value={currentFieldComponentValue.helpText.textValue}
              disabled={isMultiFieldSelection}
              onChange={(e) => handleHelpTextChange(e.target.value)}
              style={{ minHeight: "200px" }}
            />
          </>
        )}
      </div>
      {messageProps && (
        <MessageBox hideMessageBox={closeMessage} {...messageProps} />
      )}
    </div>
  );
};
